/**
 * Admin API Client for PostgreSQL Migration
 *
 * This client calls the standalone backend API.
 * Paths use the route name as the first segment:
 *   e.g. /product/auctions/active -> {API_BASE}/product/auctions/active
 *
 * The backend wraps responses in { code, status, msg, data }.
 * This client unwraps the envelope and returns just the `data` field.
 */

import {getAuth, onAuthStateChanged, type User} from 'firebase/auth'
import {API_HOST_URL} from 'components/constants'
import {getAppCheckHeaders} from './appCheck'

const API_BASE = API_HOST_URL
const API_REQUEST_TIMEOUT_MS = 15000
const AUTH_TOKEN_TIMEOUT_MS = 8000

export type ApiRequestOptions = {
  signal?: AbortSignal
}

type BrowserWithDadeAuth = Window & {
  __DADE_GET_ID_TOKEN__?: () => Promise<string | null>
}

export function getApiBaseUrl(): string {
  return API_BASE
}

export class ApiClientError extends Error {
  status: number
  details?: unknown
  body?: unknown

  constructor(
    message: string,
    status: number,
    details?: unknown,
    body?: unknown
  ) {
    super(message)
    this.name = 'ApiClientError'
    this.status = status
    this.details = details
    this.body = body
  }
}

function extractErrorMessage(
  body: Record<string, unknown> | undefined,
  fallback: string
): string {
  const details = body?.data

  if (Array.isArray(details) && details.length > 0) {
    return details.map(String).join('\n')
  }

  if (typeof details === 'string' && details.trim()) {
    return details
  }

  if (typeof body?.msg === 'string' && body.msg.trim()) {
    return body.msg
  }

  if (typeof body?.message === 'string' && body.message.trim()) {
    return body.message
  }

  return fallback
}

function buildApiError(
  status: number,
  statusText: string,
  body?: Record<string, unknown>
): ApiClientError {
  return new ApiClientError(
    extractErrorMessage(body, statusText),
    status,
    body?.data,
    body
  )
}

function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  message: string
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new Error(message))
    }, timeoutMs)

    promise
      .then((value) => {
        window.clearTimeout(timeoutId)
        resolve(value)
      })
      .catch((error) => {
        window.clearTimeout(timeoutId)
        reject(error)
      })
  })
}

async function fetchWithTimeout(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const controller = new AbortController()
  const externalSignal = init?.signal
  const abortFromCaller = () => controller.abort(externalSignal?.reason)
  if (externalSignal?.aborted) {
    controller.abort(externalSignal.reason)
  } else {
    externalSignal?.addEventListener('abort', abortFromCaller, {once: true})
  }
  const timeoutId = window.setTimeout(() => {
    controller.abort()
  }, API_REQUEST_TIMEOUT_MS)

  try {
    const send = (requestInit?: RequestInit) =>
      fetch(input, {
        ...requestInit,
        signal: controller.signal
      })

    let response = await send(init)
    if (await shouldRetryWithFreshAppCheckToken(response)) {
      const headers = new Headers(init?.headers)
      const freshAppCheckHeaders = await getAppCheckHeaders(true)
      Object.entries(freshAppCheckHeaders).forEach(([key, value]) => {
        headers.set(key, value)
      })
      response = await send({
        ...init,
        headers
      })
    }

    return response
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiClientError(
        'The API request timed out. Please refresh and try again.',
        408
      )
    }

    throw error
  } finally {
    window.clearTimeout(timeoutId)
    externalSignal?.removeEventListener('abort', abortFromCaller)
  }
}

async function shouldRetryWithFreshAppCheckToken(
  response: Response
): Promise<boolean> {
  if (response.status !== 401 || typeof response.clone !== 'function') {
    return false
  }

  try {
    const error = (await response.clone().json()) as Record<string, unknown>
    const message = extractErrorMessage(error, '')
    return /app check token|required.*app check|invalid app check/i.test(
      message
    )
  } catch {
    return false
  }
}

async function getIdToken(): Promise<string | null> {
  try {
    if (typeof window !== 'undefined') {
      const getRegisteredToken = (window as BrowserWithDadeAuth)
        .__DADE_GET_ID_TOKEN__
      if (typeof getRegisteredToken === 'function') {
        const token = await getRegisteredToken()
        if (token) {
          return token
        }
      }
    }

    const auth = getAuth()
    const resolveCurrentUser = async (): Promise<User | null> => {
      if (auth.currentUser) {
        return auth.currentUser
      }

      return new Promise<User | null>((resolve) => {
        if (typeof window === 'undefined') {
          resolve(auth.currentUser)
          return
        }

        const timeoutId = window.setTimeout(() => {
          unsubscribe()
          resolve(auth.currentUser)
        }, 5000)

        const unsubscribe = onAuthStateChanged(
          auth,
          (user) => {
            window.clearTimeout(timeoutId)
            unsubscribe()
            resolve(user)
          },
          () => {
            window.clearTimeout(timeoutId)
            unsubscribe()
            resolve(auth.currentUser)
          }
        )
      })
    }

    const user = await resolveCurrentUser()
    if (!user) return null
    return await withTimeout(
      user.getIdToken(),
      AUTH_TOKEN_TIMEOUT_MS,
      'Authentication token request timed out.'
    )
  } catch {
    return null
  }
}

export async function getApiHeaders(): Promise<Record<string, string>> {
  const token = await getIdToken()
  const headers: Record<string, string> = await getAppCheckHeaders()
  if (token) headers['Authorization'] = `Bearer ${token}`
  return headers
}

/**
 * Unwrap the backend response envelope.
 * Backend returns { code, status, msg, data }.
 * We return `data` if present; otherwise the full body for non-envelope responses.
 */
function unwrapEnvelope<T>(body: Record<string, unknown>): T {
  if (body && typeof body === 'object' && 'code' in body && 'data' in body) {
    return body.data as T
  }
  return body as T
}

export async function apiGet<T>(
  path: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const headers = await getApiHeaders()
  headers['Content-Type'] = 'application/json'

  const res = await fetchWithTimeout(`${API_BASE}${path}`, {
    headers,
    cache: 'no-store',
    signal: options.signal
  })

  if (!res.ok) {
    const error = (await res
      .json()
      .catch(() => ({message: res.statusText}))) as Record<string, unknown>
    throw buildApiError(res.status, res.statusText, error)
  }

  const body = await res.json()
  return unwrapEnvelope<T>(body)
}

export async function apiGetBlob(
  path: string
): Promise<{blob: Blob; fileName?: string}> {
  const headers = await getApiHeaders()

  const res = await fetchWithTimeout(`${API_BASE}${path}`, {
    headers,
    cache: 'no-store'
  })

  if (!res.ok) {
    const errorText = await res.text().catch(() => res.statusText)
    let error = {message: errorText || res.statusText} as Record<
      string,
      unknown
    >
    try {
      error = JSON.parse(errorText) as Record<string, unknown>
    } catch {
      // Keep the raw response text as the error message.
    }
    throw buildApiError(res.status, res.statusText, error)
  }

  const contentDisposition = res.headers.get('content-disposition') || ''
  const fileNameMatch =
    contentDisposition.match(/filename\*=UTF-8''([^;]+)/i) ||
    contentDisposition.match(/filename="?([^";]+)"?/i)
  const fileName = fileNameMatch?.[1]
    ? decodeURIComponent(fileNameMatch[1])
    : undefined

  return {
    blob: await res.blob(),
    fileName
  }
}

export async function apiPost<T>(path: string, data?: unknown): Promise<T> {
  const headers = await getApiHeaders()
  headers['Content-Type'] = 'application/json'

  const res = await fetchWithTimeout(`${API_BASE}${path}`, {
    method: 'POST',
    headers,
    body: data ? JSON.stringify(data) : undefined
  })

  if (!res.ok) {
    const error = (await res
      .json()
      .catch(() => ({message: res.statusText}))) as Record<string, unknown>
    throw buildApiError(res.status, res.statusText, error)
  }

  const body = await res.json()
  return unwrapEnvelope<T>(body)
}

export async function apiPut<T>(path: string, data: unknown): Promise<T> {
  const headers = await getApiHeaders()
  headers['Content-Type'] = 'application/json'

  const res = await fetchWithTimeout(`${API_BASE}${path}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data)
  })

  if (!res.ok) {
    const error = (await res
      .json()
      .catch(() => ({message: res.statusText}))) as Record<string, unknown>
    throw buildApiError(res.status, res.statusText, error)
  }

  const body = await res.json()
  return unwrapEnvelope<T>(body)
}

export async function apiPatch<T>(path: string, data: unknown): Promise<T> {
  const headers = await getApiHeaders()
  headers['Content-Type'] = 'application/json'

  const res = await fetchWithTimeout(`${API_BASE}${path}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(data)
  })

  if (!res.ok) {
    const error = (await res
      .json()
      .catch(() => ({message: res.statusText}))) as Record<string, unknown>
    throw buildApiError(res.status, res.statusText, error)
  }

  const body = await res.json()
  return unwrapEnvelope<T>(body)
}

export async function apiDelete(path: string): Promise<void> {
  const headers = await getApiHeaders()
  headers['Content-Type'] = 'application/json'

  const res = await fetchWithTimeout(`${API_BASE}${path}`, {
    method: 'DELETE',
    headers
  })

  if (!res.ok) {
    const error = (await res
      .json()
      .catch(() => ({message: res.statusText}))) as Record<string, unknown>
    throw buildApiError(res.status, res.statusText, error)
  }
}
